import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  StaffSurveyCategory,
  StaffSurveyCodeRequest,
  StaffSurveyForm,
  StaffSurveyFormResponse,
  StaffSurveyOverview,
  StaffSurveyOverviewDetail,
  StaffSurveyQuestion,
  StaffSurveyQuestionList,
  StaffSurveyService,
  StaffSurveySubmission,
  StaffSurveySubmissionWithCode,
} from "@/api";
import _ from "lodash";

type StaffSruvey = {
  beginAt: string | undefined;
  endAt: string | undefined;
  categories: Array<StaffSurveyCategory>;
  activeForm: StaffSurveyFormResponse | null;
  selectedForm: StaffSurveyFormResponse | null;
  staffSurveyForms: Array<StaffSurveyForm>;
  overview: StaffSurveyOverview | null;
  summaryList: Array<StaffSurveySubmission>;
  staffSurveySubmissionDetails: StaffSurveyOverviewDetail | null;
};

const state: StaffSruvey = {
  beginAt: undefined,
  endAt: undefined,
  categories: [],
  activeForm: null,
  selectedForm: null,
  staffSurveyForms: [],
  overview: null,
  summaryList: [],
  staffSurveySubmissionDetails: null,
};

const getters: GetterTree<StaffSruvey, RootState> = {
  beginAt: state => state.beginAt,
  endAt: state => state.endAt,
  categories: state => state.categories,
  activeForm: state => state.activeForm,
  selectedForm: state => state.selectedForm,
  staffSurveyForms: state => state.staffSurveyForms,
  overviewDetails: state => state.overview,
  summaryList: state => state.summaryList,
  staffSurveySubmissionDetails: state => state.staffSurveySubmissionDetails,
};

const mutations: MutationTree<StaffSruvey> = {
  SET_BEGIN_AT: (state, beginAt) => {
    state.beginAt = beginAt;
  },
  SET_END_AT: (state, endAt) => {
    state.endAt = endAt;
  },
  SET_CATEGORIES: (state, categories) => {
    const list = _.sortBy(categories, ["id"], ["asc"]);
    state.categories = list;
  },
  SET_ACTIVE_FORM: (state, form) => {
    state.activeForm = form;
  },
  SET_SELECTED_FORM: (state, form) => {
    state.selectedForm = form;
  },
  SET_STAFF_SURVEY_FORMS: (state, forms) => {
    state.staffSurveyForms = forms;
  },
  SET_OVERVIEW: (state, overview) => {
    state.overview = overview;
  },
  SET_SUMMARY_LIST: (state, summaryList) => {
    state.summaryList = summaryList;
  },
  SET_STAFF_SURVEY_SUBMISSION_DETAILS: (state, details) => {
    state.staffSurveySubmissionDetails = details;
  },
};

const actions: ActionTree<StaffSruvey, RootState> = {
  setBeginAt: ({ commit }, beginAt?: string) => {
    commit("SET_BEGIN_AT", beginAt);
  },
  setEndAt: ({ commit }, endAt?: string) => {
    commit("SET_END_AT", endAt);
  },
  getCategories: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_CATEGORIES", []);
      return;
    }

    try {
      const categories = await StaffSurveyService.getStaffSurveyCategories({
        companyId,
      });
      commit("SET_CATEGORIES", categories);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getStaffSurveyForms: async ({ commit, rootState }, slug: string) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_STAFF_SURVEY_FORMS", []);
      return;
    }

    try {
      const forms = await StaffSurveyService.getStaffSurveyFormList({
        companyId,
        slug,
      });
      commit("SET_STAFF_SURVEY_FORMS", forms);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  createStaffSurveyForm: async (
    { commit, dispatch, rootState },
    { slug, requestBody }: { slug: string; requestBody: StaffSurveyForm },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    requestBody.companyId = companyId;
    requestBody.createdAt = new Date().toISOString();
    try {
      await StaffSurveyService.createStaffSurveyForm({
        companyId,
        slug,
        requestBody,
      });

      dispatch("getStaffSurveyForms", slug);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getActiveStaffSurveyForm: async ({ commit, rootState }, slug: string) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      const form = await StaffSurveyService.getActiveStaffForm({
        companyId,
        slug,
      });
      commit("SET_ACTIVE_FORM", form);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getStaffSurveyForm: async (
    { commit, rootState },
    { formId, slug }: { formId: number; slug: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      const form = await StaffSurveyService.getStaffSurveyForm({
        companyId,
        slug,
        formId,
      });
      commit("SET_SELECTED_FORM", form);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateStaffSurveyForm: async (
    { commit, dispatch, rootState },
    {
      formId,
      slug,
      requestBody,
    }: { formId: number; slug: string; requestBody: StaffSurveyForm },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      await StaffSurveyService.updateStaffSurveyForm({
        companyId,
        slug,
        formId,
        requestBody,
      });
      dispatch("getStaffSurveyForms", slug);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  archiveStaffSurveyForm: async (
    { commit, dispatch, rootState },
    { formId, slug }: { formId: number; slug: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }
    try {
      await StaffSurveyService.archiveStaffSurveyForm({
        companyId,
        slug,
        formId,
      });
      dispatch("getStaffSurveyForms", slug);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  createStaffSruveyFormQuestion: async (
    { commit, dispatch, rootState },
    {
      formId,
      slug,
      requestBody,
    }: { formId: number; slug: string; requestBody: StaffSurveyQuestion },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      await StaffSurveyService.createStaffSurveyQuestion({
        companyId,
        formId,
        slug,
        requestBody,
      });
      dispatch("getStaffSurveyForm", { formId, slug });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateStaffSruveyFormQuestion: async (
    { commit, dispatch, rootState },
    {
      formId,
      slug,
      requestBody,
    }: { formId: number; slug: string; requestBody: StaffSurveyQuestion },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      await StaffSurveyService.updateStaffSurveyQuestion({
        companyId,
        formId,
        slug,
        requestBody,
      });
      dispatch("getStaffSurveyForm", { formId, slug });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  sortStaffSruveyFormQuestion: async (
    { commit, dispatch, rootState },
    {
      formId,
      slug,
      requestBody,
    }: { formId: number; slug: string; requestBody: StaffSurveyQuestionList },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }
    try {
      await StaffSurveyService.updateStaffSurveyQuestionOrder({
        companyId,
        formId,
        slug,
        requestBody,
      });
      dispatch("getStaffSurveyForm", { formId, slug });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deleteStaffSruveyFormQuestion: async (
    { commit, dispatch, rootState },
    {
      formId,
      slug,
      requestBody,
    }: { formId: number; slug: string; requestBody: StaffSurveyQuestion },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      requestBody.slug = slug;
      await StaffSurveyService.archiveStaffSurveyQuestion({
        companyId,
        formId,
        slug,
        requestBody,
      });
      dispatch("getStaffSurveyForm", { formId, slug });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  createStaffSurveySubmission: async (
    { commit, rootState },
    { slug, requestBody }: { slug: string; requestBody: StaffSurveySubmission },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      await StaffSurveyService.createStaffSurveySubmission({
        companyId,
        slug,
        requestBody,
      });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getStaffSurveySubmission: async (
    { commit, rootState },
    {
      submissionId,
      slug,
    }: {
      submissionId: number;
      slug: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      await StaffSurveyService.getStaffSurveySubmissionDetails({
        companyId,
        submissionId,
        slug,
      });
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getStaffSurveySubmissions: async (
    { commit, rootState },
    { slug }: { slug: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      const summaries = await StaffSurveyService.getStaffSurveySubmissionList({
        companyId,
        slug,
      });
      commit("SET_SUMMARY_LIST", summaries);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getStaffSurveyOverviews: async (
    { commit, rootState },
    { slug, beginAt, endAt }: { slug: string; beginAt: string; endAt: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      const overview = await StaffSurveyService.getStaffSurveyOverviewList({
        companyId,
        slug,
        beginAt,
        endAt,
      });
      commit("SET_OVERVIEW", overview);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getStaffSurveySubmissionDetails: async (
    { commit, rootState },
    {
      overviewId,
      slug,
      beginAt,
      endAt,
    }: { overviewId: number; slug: string; beginAt: string; endAt: string },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }

    try {
      const details = await StaffSurveyService.getStaffSurveyOverviewDetails({
        companyId,
        overviewId,
        slug,
        beginAt,
        endAt,
      });
      commit("SET_STAFF_SURVEY_SUBMISSION_DETAILS", details);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getStaffSurveyFormByCode: async ({ commit }, { code }: { code: string }) => {
    try {
      const form = await StaffSurveyService.getStaffSurveyFormByCode({
        code,
      });
      commit("SET_SELECTED_FORM", form);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  createStaffSurveySubmissionByCode: async (
    { commit },
    { requestBody }: { requestBody: StaffSurveySubmissionWithCode },
  ) => {
    try {
      const form = await StaffSurveyService.createStaffSurveySubmissionWithCode(
        {
          requestBody,
        },
      );
      commit("SET_SELECTED_FORM", form);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  sendStaffSurveyLink: async (
    { commit, rootState },
    { requestBody }: { requestBody: StaffSurveyCodeRequest },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }
    try {
      const form = await StaffSurveyService.sendStaffSurveyLink({
        companyId,
        requestBody,
      });
      commit("SET_SELECTED_FORM", form);
      return form;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  createStaffSurveyCategory: async (
    { commit, rootState, dispatch },
    { requestBody }: { requestBody: StaffSurveyCategory },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }
    try {
      await StaffSurveyService.createStaffSurveyCategory({
        companyId,
        requestBody,
      });
      dispatch("getCategories");
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateStaffSurveyCategory: async (
    { commit, rootState, dispatch },
    { requestBody }: { requestBody: StaffSurveyCategory },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }
    try {
      await StaffSurveyService.updateStaffSurveyCategory({
        companyId,
        requestBody,
      });
      dispatch("getCategories");
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deleteStaffSurveyCategory: async (
    { commit, rootState, dispatch },
    { requestBody }: { requestBody: StaffSurveyCategory },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (companyId == null) {
      commit("SET_ACTIVE_FORM", null);
      return;
    }
    try {
      await StaffSurveyService.deleteStaffSurveyCategory({
        companyId,
        requestBody,
      });
      dispatch("getCategories");
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const staffSurvey: Module<StaffSruvey, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
